import React, { Component } from 'react'
import MasterService from '../../services/MasterService';
import { toast } from 'react-toastify';
import DealerProff from '../../webroot/images/dealer-proff.jpg'
import NotEqualImg from '../../webroot/images/not-equal-img.svg'
import EqualImg from '../../webroot/images/equal-img.svg'
  
class LoanAssignmentRecordPopup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchText: '',
      editedAllocations: {}, // Stores local changes to allocated amounts
      loading: false,
      loading_reset: false,
      error: '',
      assignedLeads: props.record?.loan_match_details || [],
      searchResults: [],
      isSearching: false
    };
    this.hasMounted = false;
  }

  componentDidMount() {
    if (!this.hasMounted) {
      this.hasMounted = true;
      this.fetchLeads('');
    }
  }

  handleSearchChange = (e) => {
    const searchText = e.target.value;
    this.setState({ searchText }, () => {
      this.fetchLeads(searchText);
    });
  }

  fetchLeads = async (searchText) => {
    const { record } = this.props;
    if (!record || !record.dealer_id) return;

    this.setState({ isSearching: true });
    try {
      const response = await MasterService.getLeadList(record.dealer_id, searchText);
      // The response structure is { data: { get_lead_list: { data: [...] } } }
      const leads = response?.data?.get_lead_list?.data || [];
      
      if (Array.isArray(leads)) {
        // Map API response to match component's loan structure
        const mappedResults = leads.map(lead => ({
          id: `lead_${lead.lead_id}_${lead.type}`, // Unique ID for UI/State
          loanId: lead.lead_id,
          dueAmount: lead.due_amount,
          dueDate: lead.due_date,
          type: lead.type,
          allocatedAmount: 0 // Default for new leads
        }));
        this.setState({ searchResults: mappedResults });
      } else {
        this.setState({ searchResults: [] });
      }
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      this.setState({ isSearching: false });
    }
  }

  handleSelectLead = (lead) => {
    const { assignedLeads } = this.state;
    
    // Check if lead is already assigned with the same type
    const alreadyAssigned = assignedLeads.find(l => l.loanId === lead.loanId && l.type === lead.type);
    if (alreadyAssigned) {
      return; // Already assigned with same type, do nothing
    }
    
    // Check if there's already a lead with the same loan ID but different type
    const conflictingLead = assignedLeads.find(l => 
      l.loanId === lead.loanId && l.type !== lead.type
    );
    
    if (conflictingLead) {
      // Show error toast for conflicting selection
      toast.error(`Cannot select ${lead.type} for Loan ID ${lead.loanId}. ${conflictingLead.type} is already selected.`);
      return;
    }
    
    // No conflicts, add the lead
    this.setState({
      assignedLeads: [...assignedLeads, lead],
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.record !== prevProps.record) {
      this.setState({ 
        editedAllocations: {},
        assignedLeads: this.props.record?.loan_match_details || [],
        searchText: '',
        searchResults: []
      });
    }
  }

  handleAllocationChange = (loanId, amount) => {
    this.setState(prevState => ({
      editedAllocations: {
        ...prevState.editedAllocations,
        [loanId]: amount
      }
    }));
  }

  handleSaveAssignment = async () => {
    const { record } = this.props;
    const { editedAllocations, assignedLeads } = this.state;

    // Prepare payload for all loans in the assignedLeads
    const payload = assignedLeads.map(loan => ({
      upload_proof_id: record.id,
      allocated_loan_id: loan.loanId,
      due_amount: loan.dueAmount,
      allocated_amount: parseFloat(editedAllocations[loan.id] !== undefined ? editedAllocations[loan.id] : loan.allocatedAmount || 0),
      type: loan.type,
      loan_due_date: loan.dueDate
    }));

    this.setState({ loading: true, error: '' });
    try {
      const response = await MasterService.editLoanMatch(payload);
      if (response) {
        // MasterService handles the success toast
        if (this.props.onSuccess) this.props.onSuccess();
        
        // Refresh the lead list after successful save
        await this.fetchLeads(this.state.searchText);
      }
    } catch (err) {
      console.error('Error saving loan assignment:', err);
      // MasterService handles the error toast
    } finally {
      this.setState({ loading: false });
    }
  }

  handleReset = async () => {
    const { record } = this.props;
    
    // Call the delete_match API first
    try {
      this.setState({ loading_reset: true });
      const response = await MasterService.deleteMatch(record.id, 'loan');
      if (response) {
        // MasterService handles the success toast
        // Reset the state after successful API call
        this.setState({ 
          editedAllocations: {},
          assignedLeads: [],
          searchText: '',
          searchResults: []
        });
        
        // Call onSuccess to refresh the parent component
        if (this.props.onSuccess) this.props.onSuccess();
        
        // Refresh the lead list after successful reset
        await this.fetchLeads('');
      }
    } catch (err) {
      console.error('Error resetting loan assignment:', err);
      // MasterService handles the error toast
    } finally {
      this.setState({ loading_reset: false });
    }
  }

  formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `Rp ${parseFloat(amount).toLocaleString('id-ID')}`;
  };

  formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  calculateTotalAllocated = () => {
    const { assignedLeads, editedAllocations } = this.state;
    if (!assignedLeads) return 0;

    return assignedLeads.reduce((sum, loan) => {
      const edited = editedAllocations[loan.id];
      return sum + parseFloat(edited !== undefined ? edited : loan.allocatedAmount || 0);
    }, 0);
  }

  hasValidAdjustmentAmount = () => {
    const { assignedLeads, editedAllocations } = this.state;
    if (!assignedLeads) return false;

    return assignedLeads.some(loan => {
      const edited = editedAllocations[loan.id];
      const amount = parseFloat(edited !== undefined ? edited : loan.allocatedAmount || 0);
      return amount > 0;
    });
  }

  render() {
    const { record } = this.props;
    if (!record) return null;

    const { ocr_data = {}, amount_received = 0 ,loan_match_details = [] } = record;
    const { assignedLeads, searchResults, searchText, isSearching } = this.state;
    const totalAllocated = this.calculateTotalAllocated();
    const amountReceived = parseFloat(amount_received);
    const isMatched = Math.abs(totalAllocated - amountReceived) < 0.01;

    let displayedLeads = searchResults;
    console.log("displayedLeads", displayedLeads);
    console.log("loan_match_details", loan_match_details);
    console.log("this view", this.props.viewOnly );
    if(this.props.viewOnly){
      displayedLeads = loan_match_details;
    }
    return (
      <React.Fragment>
        <div className='bank-recording-match-data'>
          <div className='row align-item-center'>
            <div className='col-md-6'>
              {assignedLeads.length > 0 ? (
                assignedLeads.map((loan, index) => (
                  <div key={loan.id || index} className="m-lg-b">
                    <span className='macth-data-heading'>Loan Id : {loan.loanId}</span>
                    <div className='matching-detail-list'>
                      <table>
                        <thead>
                          <tr>
                            <th>Criteria</th>
                            <th>Loan detail</th>
                            <th>Dealer Record</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Date</td>
                            <td>{this.formatDate(ocr_data.ocrDate)}</td>
                            <td>{this.formatDate(loan.dueDate)}</td>
                          </tr>
                          <tr>
                            <td>Amount</td>
                            <td>{this.formatCurrency(ocr_data.ocrAmount)}</td>
                            <td>{this.formatCurrency(loan.dueAmount)}</td>
                          </tr>
                      
                          <tr>
                            <td>Type</td>
                            <td>{loan.type || 'N/A'}</td>
                            <td>{'--'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              ) : (
                <div className="m-lg-b">
                  <span className='macth-data-heading'>Loan Id : {record.uploadProofLoanId || record.upload_proof_loan_id || 'N/A'}</span>
                  <div className='matching-detail-list'>
                    <table>
                      <thead>
                        <tr>
                          <th>Criteria</th>
                          <th>Bank Record</th>
                          <th>Dealer Record</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Date</td>
                          <td>{'N/A'}</td>
                          <td>{this.formatDate(ocr_data.ocrDate)}</td>
                        </tr>
                        <tr>
                          <td>Amount</td>
                          <td>{'N/A'}</td>
                          <td>{this.formatCurrency(ocr_data.ocrAmount)}</td>
                        </tr>
                        <tr>
                          <td>Type</td>
                          <td>{'N/A'}</td>
                          <td>{'N/A'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div className='col-md-6'>
              <span className='macth-data-heading'>Upload Proof</span>
              <div className='dealer-prof-outer'>
                <img src={record.upload_proof_url || DealerProff} alt='Upload Proof' style={{ maxWidth: '100%' }} />
              </div>
            </div>
          </div>
          

          <div className='row m-lg-t'>
            <div className='col-md-6'>
              {!this.props.viewOnly ? <fieldset className="search-bx">
                <div className="material">
                  <input
                    type="text"
                    placeholder="Search by Loan ID"
                    className="form-input"
                    value={this.state.searchText}
                    onChange={this.handleSearchChange}
                  />
                  <i className="ic-search"></i>
                  {isSearching && <span className="search-loader">Loading...</span>}
                </div>
              </fieldset> : null}

              <div className='loan-assignment-detail-table'>
                <table>
                  <thead>
                    <tr>
                      <th>Loan ID</th>
                      <th>Type</th>
                      <th>Expected Amount</th>
                      <th>Due Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedLeads.map((loan) => (
                      <tr key={loan.id}>
                        <td>{loan.loanId}</td>
                        <td className="text-capitalize">{loan.type}</td>
                        <td>{this.formatCurrency(loan.dueAmount)}</td>
                        <td>{this.formatDate(loan.dueDate)}</td>
                        <td>
                          <span 
                            className='action-arrow' 
                            style={{ cursor: 'pointer' }}
                            onClick={() => this.handleSelectLead(loan)}
                          >
                            <i className='ic-arrow_forward'></i>
                          </span>
                        </td>
                      </tr>
                    ))}
                    {displayedLeads.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center">
                          {isSearching ? 'Searching leads...' : 'No leads found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className='col-md-6'>
              <div className='loan-detail-adjustment-table'>
                <table>
                  <thead>
                    <tr>
                      <th>Loan Details</th>
                      <th>Adjust Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedLeads.map((loan) => (
                      <tr key={loan.id}>
                        <td>
                          <div className='adjustment-loan-detail-data'>
                            <span>Due Amount: {this.formatCurrency(loan.dueAmount)}</span>
                            <ul>
                              <li>Loan ID: {loan.loanId}</li>
                              <li className="text-capitalize">{loan.type}</li>
                              <li>{this.formatDate(loan.dueDate)}</li>
                            </ul>
                          </div>
                        </td>
                        <td>
                          <fieldset className="">
                            <div className="material">
                              <input
                                type="number"
                                placeholder="Amount"
                                className="form-input"
                                value={this.state.editedAllocations[loan.id] !== undefined ? this.state.editedAllocations[loan.id] : loan.allocatedAmount}
                                onChange={!this.props.viewOnly ? (e) => this.handleAllocationChange(loan.id, e.target.value) : undefined}
                                readOnly={this.props.viewOnly}
                              />
                            </div>
                          </fieldset>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className='allocate-recieved-amt'>
                <div className='allocate-amt-txt'>
                  <span>{this.formatCurrency(totalAllocated)}</span>
                  <label>Allocated Amount</label>
                </div>
                <div className='equla-sign'>
                  <img src={totalAllocated === amountReceived ? EqualImg : NotEqualImg} alt={totalAllocated === amountReceived ? 'equals' : 'not equals'} />
                </div>
                <div className='allocate-amt-txt'>
                  <span>{this.formatCurrency(amountReceived)}</span>
                  <label>Received Amount</label>
                </div>
                <div className={`allocate-close-icn-bg ${isMatched ? 'success' : 'danger'}`}>
                  <i className={isMatched ? 'ic-check' : 'ic-clearclose'}></i>
                </div>
              </div>
            </div>

            {!this.props.viewOnly && (
              <div className='col-md-12 m-lg-t'>
                {this.state.error && <p className="text-danger m-sm-b">{this.state.error}</p>}
                <div className='match-btn-botton'>
                  <button
                    className='btn-primary'
                    onClick={this.handleSaveAssignment}
                    disabled={this.state.loading || !this.hasValidAdjustmentAmount()}
                  >
                    {this.state.loading ? 'Saving...' : 'Save Assignment'}
                  </button>
                  <button 
                    className='btn-line m-sm-l' 
                    onClick={this.handleReset}
                    disabled={this.state.loading_reset}
                  >
                    {this.state.loading_reset ? 'Resetting...' : 'Reset'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    )
  }
}

export default LoanAssignmentRecordPopup;